import React, { createContext, useContext, useMemo } from "react";

import { useGetToken } from "@/hooks/useGetToken";
import { useGetProjectTeamMembers } from "@/hooks/projectTeam";
import { UK_REGIONS_TO_CODE_MAP } from "@/utils/constants";

interface UserContextType {
  name: string;
  email: string;
  project: string;
  role: string;
  roleDisplayName: string;
  region: string;
  regionCode: string;
  isActive: boolean;
  // The access gate. True only when the user is present in the project-team
  // list AND has a non-blank role AND a non-blank region. There is no
  // auto-enrolment - a user not yet in the list stays gated (Access Denied)
  // until an admin adds them (via MJ-Extended-PM).
  hasAccess: boolean;
  // True when the member's Region is non-blank. Doesn't gate access on its
  // own - it's `false` whenever `hasAccess` is false anyway - but callers
  // (the Access Denied screen) use it to tell "role set, region missing"
  // apart from "role missing" so they can show a friendlier message for the
  // former. Matches MJ-Extended-PM/MJ-Tracker's UserContext.
  hasRegion: boolean;
  loading: boolean;
  fetchSuccess: boolean;
}

const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const token = useGetToken();
  const {
    isLoading: projectTeamMembersLoading,
    data: projectTeamMembersData,
    isSuccess: fetchSuccess,
  } = useGetProjectTeamMembers();

  const { email, name } = useMemo(() => {
    if (!token) return { email: "", name: "" };
    try {
      const decoded = JSON.parse(atob(token.split(".")?.[1] || ""));
      return {
        email: (decoded?.unique_name as string) || "",
        name: (decoded?.name as string) || "",
      };
    } catch {
      return { email: "", name: "" };
    }
  }, [token]);

  const user = useMemo<UserContextType | null>(() => {
    if (!token) return null;

    const teamMember = projectTeamMembersData?.find(
      (item) => item.Email?.toLowerCase() === email.toLowerCase(),
    );

    return {
      name,
      email,
      project: teamMember?.Project || "",
      role: teamMember?.Role || "",
      roleDisplayName: teamMember?.RoleDisplayName || "",
      region: teamMember?.Region || "",
      regionCode: teamMember?.Region
        ? UK_REGIONS_TO_CODE_MAP[teamMember.Region]
        : "",
      isActive: teamMember?.IsActive ?? false,
      hasAccess:
        !!teamMember &&
        !!teamMember.RoleDisplayName?.trim() &&
        !!teamMember.Region?.trim(),
      hasRegion: !!teamMember?.Region?.trim(),
      loading: projectTeamMembersLoading,
      fetchSuccess,
    };
  }, [
    token,
    email,
    name,
    projectTeamMembersLoading,
    fetchSuccess,
    projectTeamMembersData,
  ]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
